import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const pad2 = (n) => String(n).padStart(2, '0');
const dayKey = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const isoToLocalKey = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return dayKey(d);
};
const isoWeekday = (d) => (d.getDay() === 0 ? 7 : d.getDay());

const Legend = () => (
  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
    <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-green-200 border border-green-300" /> Usual home day</span>
    <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-amber-200 border border-amber-300" /> Team already playing</span>
    <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-purple-200 border border-purple-300" /> Club at capacity</span>
    <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-red-200 border border-red-300" /> Blocked / holiday</span>
    <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded-sm bg-gray-200 border border-gray-300" /> Outside season</span>
  </div>
);

const FixtureDatePicker = ({ fixture, onClose, onSave }) => {
  const [ctx, setCtx] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [month, setMonth] = useState(() => {
    const base = fixture?.match_date ? new Date(fixture.match_date) : new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });
  const [selected, setSelected] = useState(() => isoToLocalKey(fixture?.match_date));
  const [warnings, setWarnings] = useState([]);
  const [warningsLoading, setWarningsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const warningsReq = useRef(0);

  useEffect(() => {
    axios.get(`/api/fixtures/${fixture.id}/date-context`)
      .then((res) => setCtx(res.data))
      .catch((e) => setLoadError(e?.response?.data?.error || 'Failed to load date info'));
  }, [fixture.id]);

  useEffect(() => {
    if (!ctx || !selected) {
      setWarnings([]);
      return;
    }
    const req = ++warningsReq.current;
    setWarningsLoading(true);
    axios.get(`/api/fixtures/${fixture.id}/date-context`, { params: { date: selected } })
      .then((res) => {
        if (req === warningsReq.current) setWarnings(res.data.warnings || []);
      })
      .catch(() => {})
      .finally(() => {
        if (req === warningsReq.current) setWarningsLoading(false);
      });
  }, [ctx, fixture.id, selected]);

  const days = [];
  if (ctx) {
    const y = month.getFullYear();
    const m = month.getMonth();
    const offset = (new Date(y, m, 1).getDay() + 6) % 7;
    for (let i = 0; i < offset; i++) days.push(null);
    const total = new Date(y, m + 1, 0).getDate();
    for (let d = 1; d <= total; d++) days.push(new Date(y, m, d));
    while (days.length % 7 !== 0) days.push(null);
  }

  const startKey = ctx?.schedule_start_date ? isoToLocalKey(ctx.schedule_start_date) : '';
  const endKey = ctx?.schedule_end_date ? isoToLocalKey(ctx.schedule_end_date) : '';

  const classForDay = (day) => {
    const key = dayKey(day);
    const iso = isoWeekday(day);
    if ((startKey && key < startKey) || (endKey && key > endKey)) {
      return 'bg-gray-100 text-gray-400 border-gray-200';
    }
    if (ctx.flagged_dates[key]) return 'bg-red-100 text-red-800 border-red-300';
    const clubCount = ctx.club_home_dates[key] || 0;
    if (ctx.club_home_limit && clubCount >= ctx.club_home_limit) {
      return 'bg-purple-100 text-purple-800 border-purple-300';
    }
    if (ctx.busy_dates[key]) return 'bg-amber-100 text-amber-800 border-amber-300';
    if (ctx.home_days.includes(iso)) return 'bg-green-50 text-green-900 border-green-300';
    return 'bg-white text-gray-700 border-gray-200';
  };

  const titleForDay = (day) => {
    const key = dayKey(day);
    const notes = [];
    if (ctx.flagged_dates[key]) notes.push(ctx.flagged_dates[key]);
    for (const label of ctx.busy_dates[key] || []) notes.push(label);
    const clubCount = ctx.club_home_dates[key] || 0;
    if (ctx.club_home_limit && clubCount >= ctx.club_home_limit) {
      notes.push(`${ctx.club_name || 'Club'} at home-fixture limit (${clubCount}/${ctx.club_home_limit})`);
    }
    return notes.join('\n') || undefined;
  };

  const handleSave = async () => {
    if (!selected || saving) return;
    setSaving(true);
    try {
      await onSave(selected);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-lg border p-5">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <div className="text-lg font-semibold text-gray-800">
              {fixture.home_team_name} vs {fixture.away_team_name}
            </div>
            <div className="text-sm text-gray-600">Pick a fixture date — flagged days are advisory only.</div>
          </div>
          <button className="btn btn-secondary" type="button" onClick={onClose}>Close</button>
        </div>

        {loadError && <div className="text-sm text-red-700 mb-3">{loadError}</div>}
        {!ctx && !loadError && <div className="text-sm text-gray-500">Loading date info...</div>}

        {ctx && (
          <>
            <Legend />

            <div className="flex items-center justify-between mt-4">
              <div className="text-sm font-semibold text-gray-800">
                {month.toLocaleString(undefined, { month: 'long', year: 'numeric' })}
              </div>
              <div className="flex items-center gap-2">
                <button type="button" className="btn" onClick={() => setMonth((p) => new Date(p.getFullYear(), p.getMonth() - 1, 1))}>Prev</button>
                <button type="button" className="btn" onClick={() => setMonth((p) => new Date(p.getFullYear(), p.getMonth() + 1, 1))}>Next</button>
              </div>
            </div>

            <div className="mt-2 grid grid-cols-7 gap-1 text-xs font-semibold text-gray-500">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
                <div key={d} className="text-center">{d}</div>
              ))}
            </div>
            <div className="mt-1 grid grid-cols-7 gap-1">
              {days.map((day, idx) => {
                if (!day) return <div key={`empty-${idx}`} />;
                const key = dayKey(day);
                const isSelected = key === selected;
                return (
                  <button
                    key={key}
                    type="button"
                    title={titleForDay(day)}
                    onClick={() => setSelected(key)}
                    className={`h-9 rounded-md border text-sm ${classForDay(day)} ${isSelected ? 'ring-2 ring-blue-500 border-blue-500 font-bold' : ''}`}
                  >
                    {day.getDate()}
                  </button>
                );
              })}
            </div>

            <div className="mt-4 min-h-[3rem]">
              {selected && warningsLoading && (
                <div className="text-xs text-gray-500">Checking date...</div>
              )}
              {selected && !warningsLoading && warnings.length > 0 && (
                <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2">
                  <div className="text-xs font-semibold text-amber-900">
                    Warnings for {new Date(selected + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}
                  </div>
                  <ul className="mt-1 list-disc list-inside space-y-0.5 text-xs text-amber-900">
                    {warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}
              {selected && !warningsLoading && warnings.length === 0 && (
                <div className="text-xs text-green-700">
                  {new Date(selected + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })} — no issues found.
                </div>
              )}
              {!selected && <div className="text-xs text-gray-500">Select a date.</div>}
            </div>

            <div className="flex justify-end gap-2 mt-2">
              <button className="btn btn-secondary" type="button" onClick={onClose}>Cancel</button>
              <button
                className="btn btn-success"
                type="button"
                disabled={!selected || saving}
                onClick={handleSave}
              >
                {saving ? 'Saving...' : 'Save date'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FixtureDatePicker;
